import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack } from '@mui/material'

import { APP_BAR_HEIGHT } from '@/common/constants'
import { HeaderBar } from '@/components/curb/HeaderBar'
import ChartCard from '@/modules/curbParking/sharedComponents/ChartCard'
import ChartCardHeader from '@/modules/curbParking/sharedComponents/ChartCardHeader'

interface ThemedPolicyDetailsLayoutProps {
  backLink: ReactNode
  /** The policy name: a heading on the details page, a text field when editing. */
  title?: ReactNode
  status?: ReactNode
  actions?: ReactNode
  map: ReactNode
  details?: ReactNode
  disabled?: boolean
}

/**
 * The new Mobility theme look of a policy page (details, edit, create and duplicate): the back
 * link and the policy name with its actions, then the map and the policy form in two Elevate
 * cards. The old UI keeps `PolicyDetailsTemplate`.
 */
export const ThemedPolicyDetailsLayout: FC<ThemedPolicyDetailsLayoutProps> = ({
  backLink,
  title,
  status,
  actions,
  map,
  details,
  disabled = false,
}) => {
  const { t } = useTranslation()

  return (
    <Stack height="100%">
      <HeaderBar title={t('urlRouting.drawerItemPoliciesLibrary', 'Policies Library')} />
      {/*
        The `main` landmark puts the page content inside a landmark (axe `region`). The header
        stays outside it, so it keeps its `banner` role. Same space around the content as the
        Policies Library and the curb pages.
      */}
      <Box
        component="main"
        flex={1}
        minHeight={0}
        overflow="auto"
        py={2}
        px={3}
        sx={disabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
      >
        <Box ml={-1}>{backLink}</Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          useFlexGap
          sx={{ flexWrap: 'wrap', gap: 2, mt: 1, mb: 2, minHeight: 40 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
            {title}
            {status}
          </Stack>
          {actions && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {actions}
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
            alignItems: 'start',
            gap: 2,
          }}
        >
          {/*
            The map stays in view while a long form scrolls. It is as tall as the space under
            the page header, less the page padding.
          */}
          <ChartCard
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              position: { lg: 'sticky' },
              top: 0,
              height: `calc(100vh - ${APP_BAR_HEIGHT} - 32px)`,
              minHeight: 480,
            }}
          >
            <ChartCardHeader titleComponent="h3" title={t('common.geographyTitle', 'Geography')} />
            <Box
              data-testid="policy-details-map"
              sx={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              {map}
            </Box>
          </ChartCard>
          <ChartCard sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <ChartCardHeader
              titleComponent="h3"
              title={t('policiesLibrary.policyDetailsGroup', 'Policy Details')}
            />
            {details}
          </ChartCard>
        </Box>
      </Box>
    </Stack>
  )
}
